import { supabase } from '../supabaseClient';

/**
 * Billing rules, defined once because the dashboard, the payments view and the
 * cash-flow report must all agree on what "owed" means.
 */

/**
 * Statuses at which a session has concluded and can therefore be charged.
 *
 * A session only becomes debt once it has been delivered — not merely because
 * its start time has passed. A 18:00 appointment is not owed at 14:00, and a
 * session still in progress is not owed either.
 *
 * Cancellations and no-shows appear here because the practice may charge a
 * late-cancellation fee, but they are only charged when the professional
 * explicitly marks them billable (see markCancelled).
 */
export const CONCLUDED_STATUSES = ['completed', 'cancelled', 'no_show'];

/** Statuses where the session is still ahead of, or inside, the appointment. */
export const OPEN_STATUSES = ['scheduled', 'waiting', 'in_session'];

/**
 * A session still waiting for money: nothing has reached it, or part has
 * (0072). Bonificado is not in it — nothing is owed for it.
 */
export const OWING_STATUSES = ['pending', 'partial'];

/** What a session still lacks: its price, less whatever money has reached it. */
export const lacking = (row) => Math.max(
  0, Number(row?.patient_amount ?? 0) - Number(row?.patient_paid_amount ?? 0),
);

/** What a list of sessions still lacks, together. */
export const sumOwed = (rows) => (rows ?? []).reduce((total, row) => total + lacking(row), 0);

/**
 * Appointments the patient still owes money for.
 *
 * Owed = concluded + billable + not yet fully paid. What is owed on each is
 * what it lacks (lacking()), not its price: a session can be part paid. Prepaid
 * sessions are already paid, so they never surface here even though they may
 * lie in the future.
 */
export const owedAppointmentsQuery = ({ therapistId = null, from = null, to = null } = {}) => {
  let query = supabase
    .from('appointments')
    .select('id,starts_at,patient_amount,patient_paid_amount,dependent_id,therapist_id,insurance_plan_id,billing_mode')
    .in('status', CONCLUDED_STATUSES)
    .eq('is_billable', true)
    .in('patient_payment_status', OWING_STATUSES);

  if (therapistId) query = query.eq('therapist_id', therapistId);
  // Debt is bucketed by when the session happened, not when it is settled.
  if (from) query = query.gte('starts_at', from);
  if (to) query = query.lt('starts_at', to);
  return query;
};

